import { useTenant } from '@emerald-works-nova/auth'
import {
  useEvent,
  useEventsOnViewLoad
} from '@emerald-works/react-event-bus-client'
import { Box, Button, Grid, Typography, Skeleton } from '@mui/material'
import {
  AccountCircleTwoTone,
  BookmarksTwoTone,
  CollectionsBookmarkTwoTone,
  EmojiEmotionsTwoTone,
  ExploreTwoTone
} from '@mui/icons-material'
import { ActionButton } from '../../components/action-buttons'
import { routes } from '../../routes'
import { isString } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, Link } from 'react-router-dom'
import {
  AddToPlaylist,
  Container,
  ErrorDialog,
  MetaTags,
  PromotedContent,
  WithLoading
} from '../../components'
import {
  useAddToBookmarks
} from '../../components/add-to-bookmarks'
import { useAddToPlaylist } from '../../components/add-to-playlist-dialog'
import { HomePageCampaignBanner } from '../../components/campaign/home-banner'
import { HomeCard, RightGeneraldCard } from '../../components/general-card'
import HeaderUnderline from '../../components/header-underline'
import { useLike } from '../../components/like'
import { useSEO } from '../../components/seo-context'
import UserPreferences from '../../components/user-preferences'
import consts from '../../consts'
import { contentUrl } from '../../helpers/url'
import { useEventsOnViewUnload, useFormFactor, useGroup, useGoogleAdsense, useProducts, useRedirect } from '../../hooks'
import { ALL_AUTHENTICATED, ALL_CONSUMER, CONSUMER } from '../../services/cognito/userGroups'
import { campaignSlice, categorySlice, errorSlice, homeSlice, userSlice } from './../../reducers'
import { useStyles } from './style'
import { GoogleAd } from '../../components/general-card/promoted-content-card'
import { ToolkitPrivateRoute } from './../../components/toolkit-private-route'

const gridContainerLayout = { xs: 12, sm: 6, md: 12, lg: 12, xl: 12 }

const HomeBanner = () => {
  const tenant = useTenant()
  const campaign = useSelector(campaignSlice.selectors.campaignData)
  const [homeBannerClicked] = useEvent([{ eventName: 'homeBannerClicked' }])

  if (campaign?.enableHomeBanner) {
    return <HomePageCampaignBanner campaign={campaign} />
  }
  return (
    <>
      {!!tenant.banner?.length && (
        <div
          onClick={() => homeBannerClicked.trigger()}
          style={{ width: '100%', marginBottom: 20 }}
          dangerouslySetInnerHTML={{ __html: tenant.banner }}
        />
      )}
    </>

  )
}

const HOME_PAGE_SIZE = 10

const Home = () => {
  const tenant = useTenant()
  const navigate = useNavigate()
  const { isInGroup } = useGroup()
  const isConsumer = isInGroup(ALL_CONSUMER)
  const classes = useStyles({ isConsumer })

  const profile = useSelector(userSlice.selectors.selectProfile)
  const profileLoaded = useSelector(userSlice.selectors.selectIsProfileLoaded)

  const error = useSelector(errorSlice.selectors.selectError)

  const homeContent = useSelector(homeSlice.selectors.selectHomeContent)
  const randomPageHash = useSelector(homeSlice.selectors.selectRandomHash)
  const videos = useSelector(homeSlice.selectors.selectRecommendedVideos)
  const lastRead = useSelector(homeSlice.selectors.selectRecommendedRead)
  const topCategories = useSelector(homeSlice.selectors.selectRecommendedCategories)

  const categoryHierarchy = useSelector(categorySlice.selectors.selectCategoryDefaultHierarchy)

  const forYouContent = useSelector(homeSlice.selectors.selectForYouContent)
  const forYouEnabled = profile?.features?.forYouEnabled && tenant.features?.forYouEnabled

  const { isDesktop, isLargeTablet } = useFormFactor()
  const isLargeScreen = isDesktop || isLargeTablet

  const [page, setPage] = useState(1)
  const [forYouPage, setForYouPage] = useState(1)

  const linkSize = isDesktop ? 'xl' : 'l'
  const [selectedTab, setSelectedTab] = useState(forYouEnabled ? 'forYou' : 'exploreMore')

  const handleTabChange = (tab) => {
    setSelectedTab(tab)
    if (tab === 'exploreMore' && homeContent?.content?.length === 0 && !getHomeContent.hasBeenTriggered) {
      getHomeContent.trigger({
        hash: randomPageHash,
        size: HOME_PAGE_SIZE,
        from: 0
      })
    } else if (tab === 'forYou' && forYouContent?.content?.length === 0 && !getForYouContent.hasBeenTriggered) {
      getForYouContent.trigger({
        size: HOME_PAGE_SIZE,
        from: 0
      })
    }
  }

  const dispatch = useDispatch()

  const [homeViewed, homeExited] = useEvent([{ eventName: 'homeViewed' }, { eventName: 'homeExited' }])
  const { showAddToPlaylist, getPlaylistArticle } = useAddToPlaylist()

  const { handleLike, liking } = useLike({
    onSuccess: payload => dispatch(homeSlice.actions.toggleRecommend(payload)),
    from: 'homepage'
  })

  const hasNextPage = (homeContent.content.length < homeContent.total) || (forYouContent.content.length < forYouContent.total)

  const { isBookmarked, handleBookmark, bookmarking } = useAddToBookmarks(
    homeContent?.content
  )

  const isPaidConsumer = isInGroup(CONSUMER) && tenant.pk === consts.DEFAULT_CLUB_PK
  const isAuthenticated = isInGroup(ALL_AUTHENTICATED)

  const [getHomeContent] = useEvent([homeSlice.eventBus.getHomeContent])
  const [getRecommendedVideos] = useEvent([homeSlice.eventBus.getRecommendedVideos])
  const [getLastRead] = useEvent([homeSlice.eventBus.getLastRead])
  const [getRecommendedCategories] = useEvent([homeSlice.eventBus.getRecommendedCategories])
  const [getForYouContent] = useEvent([homeSlice.eventBus.getForYouContent])

  const handleCardSelect = item => navigate(contentUrl(item, 'homepage'))

  const handleNextPage = () => {
    if (selectedTab === 'forYou') {
      const nextPage = forYouPage + 1
      getForYouContent.trigger({
        size: HOME_PAGE_SIZE,
        from: (nextPage - 1) * HOME_PAGE_SIZE
      })
      setForYouPage(nextPage)
    } else {
      const nextPage = page + 1
      getHomeContent.trigger({
        hash: randomPageHash,
        size: HOME_PAGE_SIZE,
        from: (nextPage - 1) * HOME_PAGE_SIZE
      })
      setPage(nextPage)
    }
  }

  const { useAdsense } = useGoogleAdsense()

  useEffect(() => {
    if (forYouEnabled) {
      setSelectedTab('forYou')
      if (forYouContent?.content?.length === 0 && !getForYouContent.hasBeenTriggered) {
        getForYouContent.trigger({
          size: HOME_PAGE_SIZE,
          from: 0
        })
      }
    }
  }, [forYouEnabled, forYouContent, getForYouContent])

  useEventsOnViewLoad(() => {
    if (homeContent?.content?.length === 0 && !getHomeContent.hasBeenTriggered) {
      getHomeContent.trigger({
        hash: randomPageHash,
        size: HOME_PAGE_SIZE,
        from: 0
      })
    }
  }, [getHomeContent, homeContent, randomPageHash])

  useEventsOnViewLoad(() => {
    homeViewed.trigger()
  }, [homeViewed])

  useEventsOnViewUnload((duration) => {
    homeExited.trigger({ duration })
  }, [homeExited])

  useEventsOnViewLoad(() => {
    if (isInGroup(ALL_AUTHENTICATED) && !getRecommendedVideos.hasBeenTriggered && !videos) {
      getRecommendedVideos.trigger({
        hash: randomPageHash
      })
    }
  }, [randomPageHash, getRecommendedVideos, videos])

  useEventsOnViewLoad(() => {
    if (isInGroup(ALL_AUTHENTICATED)) {
      if (!getLastRead.hasBeenTriggered && !lastRead.length) {
        getLastRead.trigger()
      }
    }
  }, [getLastRead, lastRead])

  useEventsOnViewLoad(() => {
    if (isInGroup(ALL_AUTHENTICATED)) {
      if (!getRecommendedCategories.hasBeenTriggered && !topCategories.length) {
        getRecommendedCategories.trigger()
      }
    }
  }, [getRecommendedCategories, topCategories])

  const renderHomeCards = () => (
    homeContent.content.map((item, index) => (
      <React.Fragment key={index}>

        <Grid
          item
          {...gridContainerLayout}
          style={{ marginBottom: 16 }}
          key={item?.friendlyUrl ?? index}
        >
          {index === 3 && useAdsense
            ? (
              <GoogleAd location='homepage_mid_explore_more_content' />
              )
            : (
              <HomeCard
                onSelect={() => handleCardSelect(item)}
                onAddToPlaylist={showAddToPlaylist}
                onLike={() => handleLike(item)}
                actionLoading={{
                  like: liking(item),
                  bookmark: bookmarking(item)
                }}
                content={item}
                testId={`home-card-${index}`}
                onBookmark={() => handleBookmark(item)}
                bookmarked={isBookmarked(item)}
                overrides={{
                  cardContentType: classes.cardContentType,
                  time: classes.time
                }}
              />
              )}
        </Grid>
      </React.Fragment>
    ))
  )

  const renderForYouCards = () => (
    forYouContent.content.map((item, index) => (
      <React.Fragment key={index}>

        <Grid
          item
          {...gridContainerLayout}
          style={{ marginBottom: 16 }}
          key={item?.friendlyUrl ?? index}
        >
          {index === 3 && useAdsense
            ? (
              <GoogleAd location='homepage_mid_explore_more_content' />
              )
            : (
              <HomeCard
                onSelect={() => handleCardSelect(item)}
                onAddToPlaylist={showAddToPlaylist}
                onLike={() => handleLike(item)}
                actionLoading={{
                  like: liking(item),
                  bookmark: bookmarking(item)
                }}
                content={item}
                testId={`home-card-${index}`}
                onBookmark={() => handleBookmark(item)}
                bookmarked={isBookmarked(item)}
                overrides={{
                  cardContentType: classes.cardContentType,
                  time: classes.time
                }}
              />
              )}
        </Grid>
      </React.Fragment>
    ))
  )

  const navigationButtons = [
    { id: 'foryou', label: 'For You', icon: <EmojiEmotionsTwoTone />, tab: 'forYou' },
    { id: 'exploremore', label: 'Explore More', icon: <ExploreTwoTone />, tab: 'exploreMore' }
  ]

  const linkButtons = [
    { id: 'playlists', label: 'Playlists', icon: <CollectionsBookmarkTwoTone />, link: routes.playlists.path },
    { id: 'bookmarks', label: 'Bookmarks', icon: <BookmarksTwoTone />, link: routes.bookmarks.path }
  ]

  useSEO(getHomeContent.hasBeenTriggered && !getHomeContent.isWorking)

  return (
    <Container maxContentWidth={1200} inset={3}>
      <MetaTags
        title='MindTools | Home'
        description='Essential skills for an excellent career'
        canonicalLink={`${window.location.origin}/`}
        metaTags={[{ name: 'robots', content: 'noindex' }]}
      />
      <AddToPlaylist article={getPlaylistArticle()} backText='Back to Home' />
      <Box mt={2} mb={2}>
        <ErrorDialog
          open={Boolean(error)}
          message={isString(error) ? error : 'An error has occurred'}
          onClose={() => dispatch(errorSlice.actions.setError())}
        />
        {profileLoaded && !profile.onboarded && (
          <UserPreferences profile={profile} />
        )}
        <HomeBanner />
        <PromotedContent
          onCardSelect={handleCardSelect}
          onAddToPlaylist={showAddToPlaylist}
        />
        {useAdsense && (
          <Box mt={4} mb={3}>
            <GoogleAd location='homepage_under_featured_content' />
          </Box>
        )}
        <Grid container justifyContent='space-between' spacing={2}>
          {forYouEnabled && (
            <Grid
              container
              item
              mt={4}
              lg={3}
              md={3}
              direction='column'
            >
              <Box mb={1} pt={1} pb={1} sx={{ display: 'flex', alignItems: 'center' }}>
                {!profileLoaded
                  ? (
                    <Skeleton variant='rect' width='200px' height='30px' />
                    )
                  : (
                    <>
                      <AccountCircleTwoTone sx={{ fontSize: '32px' }} />
                      <Typography variant='h5' className={classes.profileName}>{profile?.name}</Typography>
                    </>
                    )}
              </Box>
              <Grid container direction='column' alignItems='flex-start'>
                {navigationButtons.map(({ id, label, icon, tab }) => (
                  <ActionButton
                    id={`${id}-action`}
                    key={`${id}-action`}
                    variant='link'
                    size={linkSize}
                    label={label}
                    startIcon={icon}
                    className={`${classes.navigationButton} ${selectedTab === tab ? 'active' : ''}`}
                    onClick={() => handleTabChange(tab)}
                  />
                ))}
                {linkButtons.map(({ id, label, icon, link }) => (
                  <ActionButton
                    id={`${id}-action`}
                    key={`${id}-action`}
                    variant='link'
                    size={linkSize}
                    label={label}
                    startIcon={icon}
                    className={classes.link}
                    component={Link}
                    to={link}
                  />
                ))}
              </Grid>
            </Grid>
          )}
          <Grid container item md={forYouEnabled ? (isAuthenticated ? 6 : 12) : (isAuthenticated ? 8 : 12)} lg={forYouEnabled ? (isAuthenticated ? 6 : 12) : (isAuthenticated ? 9 : 12)}>
            <Grid item xs={12}>
              <Box mb={3}>
                <WithLoading loading={getHomeContent.isWorking}>
                  <h1 style={isConsumer ? { marginBottom: 0 } : {}}>
                    {selectedTab === 'forYou' ? 'For you' : 'Explore More Content'}
                  </h1>
                  {isConsumer && <HeaderUnderline width={64} color='primary' />}
                </WithLoading>
              </Box>
            </Grid>
            {selectedTab === 'exploreMore'
              ? (
                <>
                  {renderHomeCards()}
                  {getHomeContent.isWorking &&
                    new Array(HOME_PAGE_SIZE).fill(null).map((item, idx) => (
                      <Grid item {...gridContainerLayout} style={{ marginBottom: 16 }} key={idx}>
                        <HomeCard
                          onSelect={() => handleCardSelect(item)}
                          content={item}
                          testId={`loading-home-card-${idx}`}
                        />
                      </Grid>
                    ))}
                </>
                )
              : (
                <>
                  {renderForYouCards()}
                  {forYouContent.isWorking &&
                    new Array(HOME_PAGE_SIZE).fill(null).map((item, idx) => (
                      <Grid item {...gridContainerLayout} style={{ marginBottom: 16 }} key={idx}>
                        <HomeCard
                          onSelect={() => handleCardSelect(item)}
                          content={item}
                          testId={`loading-for-you-card-${idx}`}
                        />
                      </Grid>
                    ))}
                </>
                )}
            {hasNextPage && (
              <Grid item xs={12}>
                <Box
                  marginTop={2}
                  marginBottom={2}
                  display='flex'
                  flexDirection='row'
                  justifyContent='center'
                >
                  <Button
                    variant='outlined'
                    style={{ width: isLargeScreen ? '300px' : '100%' }}
                    disabled={getHomeContent.isWorking || getForYouContent.isWorking}
                    onClick={handleNextPage}
                  >
                    See More
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>

          {isAuthenticated && (
            <Grid
              container
              item
              style={{ height: '100%', flex: 1 }}
              spacing={2}
              mt={4}
              md={3}
              lg={3}
              direction='row'
            >
              {isPaidConsumer && (
                <Grid item xs={12} sm={6} md={12}>
                  <RightGeneraldCard cardType='BONUS_MATERIAL' />
                </Grid>
              )}
              {(topCategories?.length > 0 || getRecommendedCategories.isWorking) &&
                <Grid item xs={12} sm={6} md={12}>
                  <RightGeneraldCard cardType='TOPICS' content={topCategories} categoryHierarchy={categoryHierarchy} />
                </Grid>}
              {(lastRead?.length > 0 || getLastRead.isWorking) &&
                <Grid item xs={12} sm={6} md={12}>
                  <RightGeneraldCard cardType='RELATED_CONTENT' content={lastRead} />
                </Grid>}
              {(videos?.length > 0 || getRecommendedVideos.isWorking) &&
                <Grid item xs={12} sm={6} md={12}>
                  <RightGeneraldCard cardType='VIDEO' content={videos} />
                </Grid>}
              {useAdsense && (
                <Grid item xs={12} sm={6} md={12}>
                  <GoogleAd location='homepage_right_sidebar' />
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
      </Box>
    </Container>
  )
}

const Page = () => {
  const { hasContentHubAccess } = useProducts()
  const { standaloneRedirect } = useRedirect()

  useEffect(() => {
    if (!hasContentHubAccess) standaloneRedirect()
  }, [hasContentHubAccess, standaloneRedirect])

  if (!hasContentHubAccess) return null

  return <ToolkitPrivateRoute element={<Home />} />
}

export {
  Page as Component
}
